import { format } from "date-fns";
import { useForm } from "react-hook-form";
import { useRouter } from "@/app/hooks/useRouter";
import { useEffect, useMemo, useRef } from "react";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { Download, FunnelX, Link as LinkIcon } from "lucide-react";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { IRotaState } from "@/app_redux/reducers/slices/auth/rota_slice";
import PWLCalendarComponent from "@/app/components/ui/Calendar/PWLCalendar";
import { IRotaOverbookingEligibleService } from "@/api_framework/api_modals/Rota";
import { useOrgCitiesListAPI } from "@/app/hooks/api_hooks/org/useOrgCitiesListAPI";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { useRotaOverbookingEligibleServicesAPI } from "@/app/hooks/api_hooks/Rota/useRotaOverbookingEligibleServicesAPI";

export interface IAppointmentOverBookingV2FilterFields {
  clinicId: string;
  clinicName: string;
  type: string;
  date: string;
}

interface IAppointmentOverBookingV2FilterProps {
  onNewClick?: () => void;
  onUploadClick?: (file: File | null) => void | Promise<void>;
  isUploadInProgress?: boolean;
  initialFilters?: IAppointmentOverBookingV2FilterFields;
  onFiltersChange?: (filters: IAppointmentOverBookingV2FilterFields) => void;
}

const EMPTY_FILTERS: IAppointmentOverBookingV2FilterFields = {
  clinicId: "",
  clinicName: "",
  type: "",
  date: "",
};

const AppointmentOverBookingV2FilterComponent: React.FC<IAppointmentOverBookingV2FilterProps> = ({
  onNewClick,
  onUploadClick,
  isUploadInProgress = false,
  initialFilters,
  onFiltersChange,
}) => {
  const { getRouteKey } = useRouter();
  const routeId = getRouteKey("ROTA", "id");
  const { routeState } = useRouteInfo(routeId);
  const rotaState: Partial<IRotaState> = routeState?.state ?? {};
  const clinicsOrg = rotaState?.clinics ?? [];
  const overbookingEligibleServices = rotaState?.overbookingEligibleServices ?? [];
  const fetchCities = useOrgCitiesListAPI("ROTA");
  const fetchOverbookingEligibleServices = useRotaOverbookingEligibleServicesAPI();
  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  const clinicItems = useSelectMenuReducer(clinicsOrg, "display_name", "id");

  const formHook = useForm<IAppointmentOverBookingV2FilterFields>({
    mode: "onChange",
    defaultValues: initialFilters ?? EMPTY_FILTERS,
  });

  useEffect(() => {
    if (!initialFilters) return;
    formHook.reset(initialFilters);
  }, [formHook, initialFilters?.clinicId, initialFilters?.clinicName, initialFilters?.type, initialFilters?.date]);

  useEffect(() => {
    fetchCities({
      withClinics: true,
      withClinicProviders: false,
      clinicOptions: {
        selectAll: true,
      },
    });
    fetchOverbookingEligibleServices();
  }, []);

  const serviceTypeItems = useMemo(() => {
    const uniqueServiceTypes = new Map<string, string>();
    overbookingEligibleServices.forEach((service: IRotaOverbookingEligibleService | string) => {
      const typeValue =
        typeof service === "string" ? service : service?.service_type || service?.name || service?.id || "";
      if (!typeValue) return;
      uniqueServiceTypes.set(typeValue, typeValue);
    });
    return Array.from(uniqueServiceTypes.keys()).map((serviceType) => ({
      id: serviceType,
      title: serviceType,
    }));
  }, [overbookingEligibleServices]);

  const handleUploadButtonClick = () => {
    if (isUploadInProgress) return;
    uploadInputRef.current?.click();
  };

  const handleSampleFileDownload = () => {
    const link = document.createElement("a");
    link.href = "/overbooking_sample_file.xlsx";
    link.download = "overbooking_sample_file.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSampleSheetOpen = () => {
    window.open(
      "https://docs.google.com/spreadsheets/d/1OTCKfWy28kTAZudS_FjqGQSmaEAbWYoGVxt6FGxf-GM/edit?gid=624088451#gid=624088451",
      "_blank",
      "noopener,noreferrer",
    );
  };

  const handleUploadFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0] ?? null;
    if (onUploadClick) {
      await onUploadClick(selectedFile);
    }
    event.target.value = "";
  };

  const handleApplyFilters = () => {
    if (!onFiltersChange) return;
    const values = formHook.getValues();
    onFiltersChange({
      clinicId: values?.clinicId ?? "",
      clinicName: values?.clinicName ?? "",
      type: values?.type ?? "",
      date: values?.date ?? "",
    });
  };

  const handleResetFilters = () => {
    formHook.reset(EMPTY_FILTERS);
    onFiltersChange?.(EMPTY_FILTERS);
  };

  const [selectedClinicId = "", selectedType = "", selectedDate = ""] = formHook.watch(["clinicId", "type", "date"]);
  const shouldShowClearFilters = [selectedClinicId, selectedType, selectedDate].some(
    (value) => `${value ?? ""}`.trim().length > 0,
  );

  return (
    <div className="bg-white relative z-30">
      <input
        ref={uploadInputRef}
        type="file"
        accept=".xls,.xlsx"
        className="hidden"
        onChange={handleUploadFileChange}
      />
      <div className="flex sm:flex-row flex-col sm:items-start justify-center gap-3">
        <div className="flex-1 flex items-center gap-2">
          <h2 className="font-semibold text-sm text-left">Overbooking Request's</h2>
          <button
            type="button"
            onClick={handleSampleFileDownload}
            title="Download sample file"
            aria-label="Download sample file"
            className="inline-flex items-center justify-center h-7 w-7 rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 hover:text-gray-800 transition-colors"
          >
            <Download className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={handleSampleSheetOpen}
            title="Open sample sheet"
            aria-label="Open sample sheet"
            className="inline-flex items-center justify-center h-7 w-7 rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 hover:text-gray-800 transition-colors"
          >
            <LinkIcon className="w-4 h-4" />
          </button>
        </div>
        <div className="flex sm:flex-row flex-col items-stretch sm:items-center sm:justify-end gap-2 w-full sm:w-auto">
          <button
            type="button"
            disabled={isUploadInProgress}
            onClick={handleUploadButtonClick}
            className="w-full sm:w-[120px] py-2 rounded-lg bg-[#F1F1F1] text-[#1F1F23] text-[12px] font-medium hover:bg-gray-200 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isUploadInProgress ? "Uploading..." : "Upload"}
          </button>
          <button
            type="button"
            onClick={onNewClick}
            className="w-full sm:w-[120px] py-2 rounded-lg bg-black text-white text-[12px] font-medium hover:opacity-90 transition-opacity"
          >
            New
          </button>
        </div>
      </div>
      <div className="mt-2 py-1 rounded-xl w-full">
        <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 w-full">
          <div className="w-full sm:w-[200px] min-w-0">
            <input hidden {...formHook.register("clinicId")} />
            <SearchableSelectMenu
              errorMessages={[{ message: "Clinic is required", type: "required" }]}
              onSelectItem={(item) => {
                formHook.setValue("clinicId", item?.id ?? "");
                formHook.setValue("clinicName", item?.title ?? "");
              }}
              fieldError={formHook.formState.errors.clinicName}
              register={formHook.register("clinicName", { required: false })}
              selectItems={clinicItems}
              placeholder="Select Clinic"
              showTooltips={false}
              showTypedErrors={false}
              showDropdownIcon
              defaultSelected={clinicItems?.find((item) => item.id === formHook.watch("clinicId"))}
              listBoxClassName="w-full z-[80]"
              className="text-sm text-gray-800"
              containerClassName="w-full"
            />
          </div>
          <div className="w-full sm:w-[200px] min-w-0">
            <input hidden {...formHook.register("type")} />
            <SearchableSelectMenu
              errorMessages={[{ message: "Type is required", type: "required" }]}
              onSelectItem={(item) => {
                formHook.setValue("type", item?.id ?? "");
              }}
              fieldError={formHook.formState.errors.type}
              register={formHook.register("type", { required: false })}
              selectItems={serviceTypeItems}
              placeholder="Select Type"
              showTooltips={false}
              showTypedErrors={false}
              showDropdownIcon
              defaultSelected={serviceTypeItems?.find((item) => item.id === formHook.watch("type"))}
              listBoxClassName="w-full z-[80]"
              className="text-sm text-gray-800"
              containerClassName="w-full"
            />
          </div>
          <div className="w-full sm:w-[200px] min-w-0 [&>div]:w-full [&>div>div]:w-full">
            <PWLCalendarComponent
              errorMessages={[{ message: "Date is required", type: "required" }]}
              register={formHook.register("date", { required: false })}
              fieldError={formHook.formState.errors.date}
              showTypedErrors={false}
              disableDatesBeforeDate={format(new Date(), "yyyy-MM-dd")}
              defaultSelectedDate={formHook.watch("date")}
              onSelectDate={(date) => {
                if (!date) return;
                formHook.setValue("date", format(date, "yyyy-MM-dd"));
              }}
              className="w-full h-9 !py-0 text-sm leading-tight focus:outline-none !focus:ring-0 !ring-gray-300"
            />
          </div>
          <div className="w-full sm:w-auto min-w-0 flex items-center gap-2">
            <button
              type="button"
              onClick={handleApplyFilters}
              className="w-full sm:w-[120px] h-9 rounded-lg bg-black text-white text-[12px] font-medium hover:opacity-90 transition-opacity"
            >
              Apply Filters
            </button>
            {shouldShowClearFilters && (
              <button
                type="button"
                title="Clear Filters"
                aria-label="Clear Filters"
                onClick={handleResetFilters}
                className="h-9 w-9 shrink-0 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 hover:text-gray-800 transition-colors flex items-center justify-center"
              >
                <FunnelX className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppointmentOverBookingV2FilterComponent;
